/**
 * Lightbox for the photo-gallery block.
 *
 * The block renders each photo as an <a href> to its full-size image, so with
 * this module dead the links still work. Here the click is intercepted and an
 * overlay opens instead, with prev/next, Escape, arrow keys, click-outside and
 * a body scroll lock.
 *
 * THE FOCUS TRAP AND FOCUS RESTORE ARE A DELIBERATE DIVERGENCE FROM THE
 * REFERENCE. Training.jsx:320-365 sets role="dialog" and aria-modal="true" — a
 * promise to assistive technology that focus is contained — while Tab moves
 * freely through the page behind it and closing drops focus to <body>. Since
 * this module is written rather than transliterated, the trap is a short loop
 * over the overlay's focusable children and the restore is one stored
 * reference. Nothing visual moves; this is a defect fix, not a design change.
 *
 * The overlay's hover colour is GOLD, not accent. The reference's `accent` is
 * gold; ours is brand blue, and this overlay is always bg-ink-deep/90 — a fixed
 * dark ground — so gold is correct here unbranched.
 *
 * The overlay is appended to <body>, not to the block. header.php gives
 * #content `relative z-10`, which opens a stacking context that a z-index on
 * any descendant can never escape — leadership.js portals its drawer for the
 * same reason.
 */

import { setScrollLock } from './scroll-lock.js';
import { icon } from './icons.js';

// One lightbox per page, so a module-level key is enough to own the lock.
const LOCK_OWNER = 'gallery-lightbox';

const OVERLAY_CLASS =
	'fixed inset-0 z-[100] flex items-center justify-center bg-ink-deep/90 p-4 backdrop-blur-sm sm:p-8';

let overlay = null;
let items = [];
let index = 0;
let lastFocused = null;

function focusable() {
	// Buttons inside a [hidden] ancestor are display:none and NOT in the tab
	// order, so they must be excluded here or the trap breaks. With a single
	// photo the nav container is hidden, and an unfiltered list would make
	// `last` an unreachable button: tabbing forward from the close button
	// would match neither branch below, preventDefault would never fire, and
	// focus would escape the overlay while aria-modal is still asserted.
	return overlay
		? Array.from(overlay.querySelectorAll('button')).filter((b) => !b.closest('[hidden]'))
		: [];
}

function render() {
	const item = items[index];
	if (!item || !overlay) return;
	const img = overlay.querySelector('[data-lb-img]');
	img.src = item.href;
	img.alt = item.dataset.alt || '';
	const nav = overlay.querySelector('[data-lb-nav]');
	if (nav) nav.hidden = items.length < 2;
}

function step(delta) {
	if (!items.length) return;
	index = (index + delta + items.length) % items.length;
	render();
}

function onKey(e) {
	if (e.key === 'Escape') {
		close();
	} else if (e.key === 'ArrowRight') {
		step(1);
	} else if (e.key === 'ArrowLeft') {
		step(-1);
	} else if (e.key === 'Tab') {
		// Contain focus, honouring the aria-modal promise.
		const f = focusable();
		if (!f.length) return;
		const first = f[0];
		const last = f[f.length - 1];
		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	}
}

function close() {
	if (!overlay) return;
	document.removeEventListener('keydown', onKey);
	setScrollLock(LOCK_OWNER, false);
	overlay.remove();
	overlay = null;
	if (lastFocused) {
		lastFocused.focus();
		lastFocused = null;
	}
}

function open(list, start, trigger) {
	items = list;
	index = start;
	lastFocused = trigger || null;

	overlay = document.createElement('div');
	overlay.className = OVERLAY_CLASS;
	overlay.setAttribute('role', 'dialog');
	overlay.setAttribute('aria-modal', 'true');
	overlay.setAttribute('aria-label', 'Photo viewer');
	overlay.innerHTML = `
		<button type="button" data-lb-close aria-label="Close"
			class="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:border-gold hover:text-gold">
			${icon('close', 'h-[18px] w-[18px]')}
		</button>
		<div data-lb-nav>
			<button type="button" data-lb-prev aria-label="Previous photo"
				class="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:border-gold hover:text-gold sm:left-6">
				${icon('arrow', 'h-5 w-5 rotate-180')}
			</button>
			<button type="button" data-lb-next aria-label="Next photo"
				class="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:border-gold hover:text-gold sm:right-6">
				${icon('arrow', 'h-5 w-5')}
			</button>
		</div>
		<img data-lb-img alt="" class="max-h-[85vh] w-auto max-w-full rounded-xl object-contain" />
	`;

	overlay.addEventListener('click', (e) => {
		if (e.target === overlay) close();
	});
	overlay.querySelector('[data-lb-close]').addEventListener('click', close);
	overlay.querySelector('[data-lb-prev]').addEventListener('click', (e) => {
		e.stopPropagation();
		step(-1);
	});
	overlay.querySelector('[data-lb-next]').addEventListener('click', (e) => {
		e.stopPropagation();
		step(1);
	});

	document.body.appendChild(overlay);
	setScrollLock(LOCK_OWNER, true);
	document.addEventListener('keydown', onKey);
	render();
	overlay.querySelector('[data-lb-close]').focus();
}

export default function initGallery() {
	document.querySelectorAll('[data-awdd-gallery]').forEach((grid) => {
		const list = Array.from(grid.querySelectorAll('[data-awdd-gallery-item]'));
		if (!list.length) return;

		grid.addEventListener('click', (e) => {
			const link = e.target.closest('[data-awdd-gallery-item]');
			if (!link || !grid.contains(link)) return;
			// A modifier click (or a non-primary button) is the visitor asking for
			// the reference's own escape hatch -- open in a new tab, new window, or
			// via the OS's own handling. preventDefault below would swallow that
			// unconditionally, defeating the docblock's stated reason for using a
			// real <a href> instead of the reference's <button>.
			if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
			e.preventDefault();
			open(list, list.indexOf(link), link);
		});
	});
}
